import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Lock, Trophy, AlertTriangle, TrendingUp, Sparkles, MapPin } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { GEOGRID_INSIGHTS } from "@/lib/geogrid-insights";
import { fireConfetti } from "@/lib/confetti";
import type { GeogridScan, GridPoint } from "@shared/schema";

interface Props {
  scan: GeogridScan;
  points: GridPoint[];
  centerLat: number;
  centerLng: number;
  keyword?: string;
  onComplete?: () => void;
}

function rankColor(rank: number | null | undefined): string {
  if (rank == null || rank > 20) return "#4b5563";
  if (rank <= 3) return "#10b981";
  if (rank <= 10) return "#f59e0b";
  return "#ef4444";
}

function rankLabel(rank: number | null | undefined): string {
  if (rank == null || rank > 20) return "—";
  return String(rank);
}

function compass(dLat: number, dLng: number): string {
  if (Math.abs(dLat) < 1e-6 && Math.abs(dLng) < 1e-6) return "center";
  const angle = (Math.atan2(dLng, dLat) * 180) / Math.PI;
  const dirs = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
  const idx = Math.round(((angle + 360) % 360) / 45) % 8;
  return dirs[idx];
}

function distanceMi(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 3958.8;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function CountUp({ value, decimals = 0, className }: { value: number; decimals?: number; className?: string }) {
  const [display, setDisplay] = useState(value);
  const fromRef = useRef(value);
  useEffect(() => {
    const from = fromRef.current;
    const start = performance.now();
    const dur = 300;
    let raf = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(from + (value - from) * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
      else fromRef.current = value;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return <span className={className}>{display.toFixed(decimals)}</span>;
}

export function GeogridScanTheater({ scan, points, centerLat, centerLng, keyword, onComplete }: Props) {
  const { toast } = useToast();
  const isProcessing = scan.status === "processing" || scan.status === "pending";
  const totalPoints = scan.gridSize * scan.gridSize;

  // Order points center-out so the reveal feels like radiating discovery.
  const orderedPoints = useMemo(() => {
    return [...points].sort((a, b) => {
      const da = distanceMi(centerLat, centerLng, parseFloat(a.lat), parseFloat(a.lng));
      const db = distanceMi(centerLat, centerLng, parseFloat(b.lat), parseFloat(b.lng));
      return da - db;
    });
  }, [points, centerLat, centerLng]);

  // Stagger reveal once status flips to completed.
  const [revealedCount, setRevealedCount] = useState(0);
  const revealStartedFor = useRef<string | null>(null);
  // Reset all reveal-related state when the active scan changes.
  useEffect(() => {
    revealStartedFor.current = null;
    awardedRef.current = false;
    setRevealedCount(0);
  }, [scan.id]);
  useEffect(() => {
    if (scan.status !== "completed" || orderedPoints.length === 0) return;
    if (revealStartedFor.current === scan.id) return;
    revealStartedFor.current = scan.id;
    setRevealedCount(0);
    let i = 0;
    const total = orderedPoints.length;
    const totalDur = 4500;
    const step = Math.max(40, totalDur / total);
    const interval = window.setInterval(() => {
      i += 1;
      setRevealedCount(i);
      if (i >= total) window.clearInterval(interval);
    }, step);
    return () => window.clearInterval(interval);
  }, [scan.status, orderedPoints.length]);

  const revealedPoints = useMemo(() => orderedPoints.slice(0, revealedCount), [orderedPoints, revealedCount]);
  const revealComplete = scan.status === "completed" && orderedPoints.length > 0 && revealedCount >= orderedPoints.length;

  const awardedRef = useRef(false);
  const onCompleteRef = useRef(onComplete);
  const toastRef = useRef(toast);
  useEffect(() => { onCompleteRef.current = onComplete; }, [onComplete]);
  useEffect(() => { toastRef.current = toast; }, [toast]);

  useEffect(() => {
    if (!revealComplete || awardedRef.current) return;
    awardedRef.current = true;
    fireConfetti(2200);
    (async () => {
      try {
        const resp = await apiRequest("POST", `/api/geogrid-scans/scan/${scan.id}/award-tokens`);
        const data = await resp.json();
        const awards: { amount: number; label: string }[] = data.awards || [];
        awards.forEach((a, idx) => {
          window.setTimeout(() => {
            toastRef.current({
              title: `+${a.amount} GeoGrid Credits`,
              description: a.label,
            });
          }, idx * 600);
        });
        queryClient.invalidateQueries({ queryKey: ["/api/token-events"] });
        queryClient.invalidateQueries({ queryKey: ["/api/locations"] });
        queryClient.invalidateQueries({ queryKey: ["/api/billing/geogrid-credits"] });
      } catch (err) {
        console.error("Failed to award tokens", err);
      }
    })();
    const t = window.setTimeout(() => onCompleteRef.current?.(), 2400);
    return () => window.clearTimeout(t);
  }, [revealComplete, scan.id]);

  // Non-linear weighted progress bar.
  const realProgress = scan.progress ?? 0;
  const [displayProgress, setDisplayProgress] = useState(0);
  useEffect(() => {
    if (revealComplete) {
      setDisplayProgress(100);
      return;
    }
    if (scan.status === "completed") {
      // Sync with reveal progress.
      const pct = orderedPoints.length === 0 ? 95 : 70 + (revealedCount / orderedPoints.length) * 30;
      setDisplayProgress(Math.min(100, pct));
      return;
    }
    // Map real → displayed through a 3-piece curve.
    const mapped =
      realProgress <= 10 ? (realProgress / 10) * 30 :
      realProgress <= 60 ? 30 + ((realProgress - 10) / 50) * 40 :
      70 + ((realProgress - 60) / 40) * 25;
    setDisplayProgress(prev => Math.max(prev, mapped));
  }, [realProgress, scan.status, revealComplete, revealedCount, orderedPoints.length]);

  // Trickle while waiting so the bar never freezes.
  useEffect(() => {
    if (scan.status === "completed") return;
    const id = window.setInterval(() => {
      setDisplayProgress(prev => {
        const cap = Math.min(95, prev + 8);
        return Math.min(cap, prev + 0.5);
      });
    }, 250);
    return () => window.clearInterval(id);
  }, [scan.status]);

  // Insights ticker.
  const [insightIdx, setInsightIdx] = useState(0);
  const [insightPaused, setInsightPaused] = useState(false);
  useEffect(() => {
    if (insightPaused) return;
    const id = window.setInterval(() => setInsightIdx(i => (i + 1) % GEOGRID_INSIGHTS.length), 7000);
    return () => window.clearInterval(id);
  }, [insightPaused]);

  // Stats from revealed slice.
  const stats = useMemo(() => {
    const ranked = revealedPoints.filter(p => p.rank != null && (p.rank as number) <= 20);
    const top3 = revealedPoints.filter(p => p.rank != null && (p.rank as number) <= 3).length;
    const avgRank = ranked.length === 0 ? 0 : ranked.reduce((s, p) => s + (p.rank as number), 0) / ranked.length;
    let best: GridPoint | null = null;
    let worst: GridPoint | null = null;
    for (const p of revealedPoints) {
      if (p.rank == null) continue;
      if (!best || (p.rank as number) < (best.rank as number)) best = p;
      if (!worst || (p.rank as number) > (worst.rank as number)) worst = p;
    }
    const labelFor = (p: GridPoint | null) => {
      if (!p) return "—";
      const d = distanceMi(centerLat, centerLng, parseFloat(p.lat), parseFloat(p.lng));
      const dir = compass(parseFloat(p.lat) - centerLat, parseFloat(p.lng) - centerLng);
      return `${d.toFixed(1)} mi ${dir}`;
    };
    return {
      scanned: revealedPoints.length,
      total: totalPoints,
      top3,
      avgRank,
      bestLabel: labelFor(best),
      worstLabel: labelFor(worst),
    };
  }, [revealedPoints, centerLat, centerLng, totalPoints]);

  // Grid SVG projection.
  const gridLats = useMemo(() => orderedPoints.map(p => parseFloat(p.lat)), [orderedPoints]);
  const gridLngs = useMemo(() => orderedPoints.map(p => parseFloat(p.lng)), [orderedPoints]);
  const radiusNum = parseFloat(scan.radius || "0") || 1;
  // Rough degree span based on radius (mi). 1° lat ≈ 69 mi.
  const span = (radiusNum * 1.4) / 69;
  const minLat = centerLat - span;
  const maxLat = centerLat + span;
  const minLng = centerLng - span / Math.cos((centerLat * Math.PI) / 180);
  const maxLng = centerLng + span / Math.cos((centerLat * Math.PI) / 180);
  const project = (lat: number, lng: number) => {
    const x = ((lng - minLng) / (maxLng - minLng)) * 100;
    const y = (1 - (lat - minLat) / (maxLat - minLat)) * 100;
    return { x, y };
  };

  // Idle pulse during processing — pick a few pin coords from a synthetic grid if no points exist yet.
  const placeholderPins = useMemo(() => {
    if (orderedPoints.length > 0) return [];
    const arr: { x: number; y: number }[] = [];
    const n = scan.gridSize;
    for (let r = 0; r < n; r++) {
      for (let c = 0; c < n; c++) {
        arr.push({ x: ((c + 0.5) / n) * 100, y: ((r + 0.5) / n) * 100 });
      }
    }
    return arr;
  }, [orderedPoints.length, scan.gridSize]);
  const [pulseSet, setPulseSet] = useState<number[]>([]);
  useEffect(() => {
    if (scan.status === "completed") return;
    const id = window.setInterval(() => {
      const pool = placeholderPins.length;
      if (pool === 0) return;
      const next: number[] = [];
      for (let i = 0; i < 4; i++) next.push(Math.floor(Math.random() * pool));
      setPulseSet(next);
    }, 700);
    return () => window.clearInterval(id);
  }, [scan.status, placeholderPins.length]);

  // Milestone cards
  const pct = orderedPoints.length === 0 ? 0 : revealedCount / orderedPoints.length;
  const showInner9 = pct >= 0.25;
  const showBelowTop3 = pct >= 0.5;
  const showLeadsAtRisk = pct >= 0.75;
  const showFinal = pct >= 1;

  const inner9Avg = useMemo(() => {
    // Inner 3x3 = closest 9 to center.
    const inner = revealedPoints.slice(0, 9).filter(p => p.rank != null && (p.rank as number) <= 20);
    if (inner.length === 0) return 0;
    return inner.reduce((s, p) => s + (p.rank as number), 0) / inner.length;
  }, [revealedPoints]);
  const belowTop3Count = useMemo(
    () => revealedPoints.filter(p => p.rank == null || (p.rank as number) > 3).length,
    [revealedPoints]
  );
  // Leads-at-risk heuristic: each area outside top 3 ≈ ~30 leads/mo lost; conservative $50/lead.
  const leadsAtRisk = belowTop3Count * 30;
  const dollarsAtRisk = leadsAtRisk * 50;

  return (
    <div className="space-y-4" data-testid="geogrid-scan-theater">
      <div className="grid lg:grid-cols-[1fr_320px] gap-4">
        {/* Grid */}
        <Card className="overflow-hidden">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-wide">
                  {revealComplete ? "Scan Complete" : "Scanning Your Market"}
                </div>
                <div className="text-lg font-semibold" data-testid="text-theater-keyword">
                  {keyword || "Keyword"}
                </div>
              </div>
              <Badge variant="outline" className="gap-1">
                <MapPin className="w-3 h-3" /> {totalPoints} pts · {radiusNum} mi
              </Badge>
            </div>

            <div className="relative w-full aspect-square rounded-lg bg-muted/30 border overflow-hidden">
              <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 w-full h-full">
                {/* Grid lines */}
                {Array.from({ length: scan.gridSize + 1 }).map((_, i) => (
                  <g key={`gl-${i}`}>
                    <line x1={(i / scan.gridSize) * 100} y1="0" x2={(i / scan.gridSize) * 100} y2="100" stroke="currentColor" strokeOpacity="0.08" strokeWidth="0.2" />
                    <line x1="0" y1={(i / scan.gridSize) * 100} x2="100" y2={(i / scan.gridSize) * 100} stroke="currentColor" strokeOpacity="0.08" strokeWidth="0.2" />
                  </g>
                ))}
                {/* Center marker */}
                <circle cx="50" cy="50" r="1.2" fill="#fb923c" />

                {/* Idle placeholder pins while waiting */}
                {orderedPoints.length === 0 && placeholderPins.map((p, idx) => (
                  <g key={`ph-${idx}`}>
                    <circle cx={p.x} cy={p.y} r="1.6" fill="none" stroke="#6b7280" strokeWidth="0.25" opacity="0.6" />
                    {pulseSet.includes(idx) && (
                      <motion.circle
                        cx={p.x}
                        cy={p.y}
                        r="1.6"
                        fill="#fb923c"
                        initial={{ opacity: 0, scale: 0.5 }}
                        animate={{ opacity: [0, 0.8, 0], scale: [0.5, 2.5, 3] }}
                        transition={{ duration: 0.9 }}
                      />
                    )}
                  </g>
                ))}

                {/* Real pins, revealing */}
                <AnimatePresence>
                  {orderedPoints.map((p, idx) => {
                    const { x, y } = project(parseFloat(p.lat), parseFloat(p.lng));
                    const revealed = idx < revealedCount;
                    return (
                      <motion.g
                        key={p.id}
                        initial={{ opacity: 0, scale: 0 }}
                        animate={{ opacity: revealed ? 1 : 0.2, scale: revealed ? 1 : 0.4 }}
                        transition={{ duration: 0.35, ease: "backOut" }}
                      >
                        <circle cx={x} cy={y} r="2.6" fill={revealed ? rankColor(p.rank) : "#374151"} stroke="#0a0a0a" strokeWidth="0.3" />
                        {revealed && (
                          <text x={x} y={y + 0.9} textAnchor="middle" fontSize="2.4" fontWeight="700" fill="#fff">
                            {rankLabel(p.rank)}
                          </text>
                        )}
                      </motion.g>
                    );
                  })}
                </AnimatePresence>
              </svg>
            </div>

            {/* Progress */}
            <div className="mt-4">
              <div className="flex items-center justify-between text-xs text-muted-foreground mb-1.5">
                <span>{revealComplete ? "Complete" : isProcessing ? "Querying Google Maps…" : "Revealing results…"}</span>
                <span data-testid="text-theater-progress">{Math.round(displayProgress)}%</span>
              </div>
              <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-gradient-to-r from-primary to-orange-400"
                  initial={false}
                  animate={{ width: `${displayProgress}%` }}
                  transition={{ duration: 0.4, ease: "easeOut" }}
                />
              </div>
            </div>

            {/* Insights ticker */}
            <div
              className="mt-4 rounded-md border bg-muted/40 px-3 py-2 overflow-hidden"
              onMouseEnter={() => setInsightPaused(true)}
              onMouseLeave={() => setInsightPaused(false)}
              data-testid="ticker-insights"
            >
              <div className="flex items-center gap-2 text-xs text-muted-foreground mb-0.5">
                <Sparkles className="w-3 h-3" /> Did you know?
              </div>
              <div className="relative h-10">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={insightIdx}
                    initial={{ y: 16, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -16, opacity: 0 }}
                    transition={{ duration: 0.45 }}
                    className="text-sm absolute inset-0 flex items-center"
                  >
                    {GEOGRID_INSIGHTS[insightIdx]}
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats sidebar */}
        <Card>
          <CardContent className="p-4 space-y-4">
            <div className="text-xs text-muted-foreground uppercase tracking-wide">Live Stats</div>
            <Stat label="Pins scanned" value={`${stats.scanned} / ${stats.total}`} />
            <Stat label="In top 3" value={<CountUp value={stats.top3} />} highlight={stats.top3 > 0} />
            <Stat label="Avg rank" value={stats.avgRank > 0 ? <CountUp value={stats.avgRank} decimals={1} /> : "—"} />
            <Stat label="Best area" value={stats.bestLabel} />
            <Stat label="Weakest area" value={stats.worstLabel} />
          </CardContent>
        </Card>
      </div>

      {/* Milestone cards */}
      <div className="grid md:grid-cols-2 gap-3">
        <AnimatePresence>
          {showInner9 && (
            <Milestone key="m1" icon={<Trophy className="w-4 h-4 text-amber-400" />} title="Inner-9 average rank">
              <div className="text-2xl font-bold" data-testid="text-milestone-inner9">
                {inner9Avg > 0 ? <CountUp value={inner9Avg} decimals={1} /> : "—"}
              </div>
              <div className="text-xs text-muted-foreground">Closest 9 grid points to your business</div>
            </Milestone>
          )}
          {showBelowTop3 && (
            <Milestone key="m2" icon={<AlertTriangle className="w-4 h-4 text-red-400" />} title="Below top 3">
              <div className="text-2xl font-bold" data-testid="text-milestone-below-top3">
                <CountUp value={belowTop3Count} /> areas
              </div>
              <div className="text-xs text-muted-foreground">Where competitors are beating you</div>
            </Milestone>
          )}
          {showLeadsAtRisk && (
            <Milestone
              key="m3"
              icon={<Lock className="w-4 h-4 text-muted-foreground" />}
              title="Estimated monthly leads at risk"
              locked
            >
              <div className="text-2xl font-bold blur-sm select-none" data-testid="text-milestone-risk">
                ${dollarsAtRisk.toLocaleString()}
              </div>
              <div className="text-xs text-muted-foreground">Unlock with a paid plan</div>
            </Milestone>
          )}
          {showFinal && (
            <Milestone key="m4" icon={<TrendingUp className="w-4 h-4 text-emerald-400" />} title="Your full report is ready">
              <Button
                size="sm"
                className="mt-1"
                onClick={() => onComplete?.()}
                data-testid="button-view-full-report"
              >
                View Full Report
              </Button>
            </Milestone>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

function Stat({ label, value, highlight }: { label: string; value: React.ReactNode; highlight?: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-muted-foreground">{label}</span>
      <motion.span
        key={String(value)}
        initial={{ scale: 1.15, color: "#fb923c" }}
        animate={{ scale: 1, color: highlight ? "#10b981" : "#ffffff" }}
        transition={{ duration: 0.4 }}
        className="font-semibold tabular-nums"
      >
        {value}
      </motion.span>
    </div>
  );
}

function Milestone({
  icon,
  title,
  children,
  locked,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
  locked?: boolean;
}) {
  return (
    <motion.div
      initial={{ y: 20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.45, ease: "backOut" }}
    >
      <Card className={locked ? "border-dashed" : ""}>
        <CardContent className="p-4">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground mb-2">
            {icon}
            {title}
          </div>
          {children}
        </CardContent>
      </Card>
    </motion.div>
  );
}
